import { z } from 'zod'
import { graphql } from '@/gql'
import { gqlRequest, type RequestSession } from './api/client'

export const RecordsQuery = graphql(`
  query GalleyRecords($restaurantId: ID!, $class: String!, $after: String) {
    restaurant(id: $restaurantId) { id canWrite: hasAccess(access: Write)
      entities(class: $class, first: 100, after: $after) {
        edges { node { id updatedAt document: attribute(key: "galley.document") } }
        pageInfo { hasNextPage endCursor }
      }
    }
  }
`)
export const CreateRecordMutation = graphql(`
  mutation GalleyCreateRecord($restaurantId: ID!, $input: RestaurantEntityInput!) {
    createRestaurantEntity(restaurantId: $restaurantId, input: $input) { id updatedAt document: attribute(key: "galley.document") }
  }
`)
export const UpdateRecordMutation = graphql(`
  mutation GalleyUpdateRecord($restaurantId: ID!, $id: ID!, $document: JSONValue!, $updatedAt: DateTime) {
    updateRestaurantEntity(restaurantId: $restaurantId, restaurantEntityId: $id,
      patch: { attributes: [{ key: "galley.document", visibility: Private, value: $document }] }, options: { expectedUpdatedAt: $updatedAt }) {
      id updatedAt document: attribute(key: "galley.document")
    }
  }
`)
export const DeleteRecordMutation = graphql(`
  mutation GalleyDeleteRecord($restaurantId: ID!, $id: ID!) { deleteRestaurantEntity(restaurantId: $restaurantId, restaurantEntityId: $id) }
`)
export type RecordEntity = { id: string; updatedAt?: string | null; document?: unknown }

export async function loadRecords(session: RequestSession, restaurantId: string, entityClass: string): Promise<RecordEntity[]> {
  const rows: RecordEntity[] = []
  let after: string | undefined
  do {
    const data = await gqlRequest(session, RecordsQuery, { restaurantId, class: entityClass, after })
    const page = data.restaurant?.entities
    if (!page) throw new Error('Restaurangen kunde inte läsas')
    rows.push(...page.edges.map((edge) => edge.node))
    after = page.pageInfo.hasNextPage ? (page.pageInfo.endCursor ?? undefined) : undefined
  } while (after)
  return rows
}
export async function createRecord(session: RequestSession, restaurantId: string, entityClass: string, document: unknown) {
  return gqlRequest(session, CreateRecordMutation, {
    restaurantId,
    input: { class: entityClass, visibility: 'Private', attributes: [{ key: 'galley.document', visibility: 'Private', value: document }] },
  })
}

export const ticketTimeSchema = z.object({
  displayId: z.string(),
  slipId: z.string(),
  completedAt: z.iso.datetime(),
  createdAt: z.string(),
  orderCode: z.string().nullable(),
  seconds: z.number().finite().nonnegative(),
  transactionChannel: z.string().nullable(),
  orderType: z.string().nullable(),
  slip: z.unknown(),
})
export type TicketTime = z.infer<typeof ticketTimeSchema>
export function ticketSeconds(createdAt: string | null | undefined, deliveryTime: string | null | undefined, completedAt: number): number {
  const created = Date.parse(createdAt ?? '')
  const delivery = Date.parse(deliveryTime ?? '')
  return Number.isFinite(created) ? Math.max(0, Math.floor((completedAt - Math.max(created, Number.isFinite(delivery) ? delivery : created)) / 1000)) : 0
}
export function readTicketTimes(rows: RecordEntity[]): TicketTime[] {
  const unique = new Map<string, TicketTime>()
  for (const row of rows) {
    const parsed = ticketTimeSchema.safeParse(row.document)
    if (parsed.success) unique.set(`${parsed.data.displayId}/${parsed.data.slipId}`, parsed.data)
  }
  return [...unique.values()]
}

export const PrinterJobMutation = graphql(`
  mutation GalleyPrintLabel($restaurantId: ID!, $input: PrinterJobInput!) {
    createPrinterJob(restaurantId: $restaurantId, input: $input) { id }
  }
`)

export function recordDate(now: number, timeZone = 'Europe/Stockholm'): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone }).format(new Date(now))
}
